import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { getPartnerDetail } from "@/api/partners/partnersApi";
import {
  getPartnerDetailQueryKey,
  queryClient,
  removePersistedQueries
} from "@/lib/react-query";
import { initTabStorage } from "@/store/tabStore";
import { getBasePath, withBasePath } from "@/utils/basePath";
import { getSubdomain } from "@/utils/location";

import { datadogRum } from "@datadog/browser-rum";

import App from "./App.tsx";

import { EXCLUDED_EMAILS_FROM_TRACKING } from "./constants/user.ts";
import { shouldSkipPartnerCheck } from "./utils/partner.ts";

import "maplibre-gl/dist/maplibre-gl.css";
import "./index.css";

/** Enable DD Logging only in Pre-Production and Production envs, and exclude automation runners (that are using headless browsers) */
const runDatadogAnalytics =
  import.meta.env.VITE_ENABLE_DATA_DOG_ANALYTICS === "true" &&
  !/headless/i.test(navigator.userAgent) &&
  (import.meta.env.MODE === "preproduction" ||
    import.meta.env.MODE === "production");

if (runDatadogAnalytics) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATA_DOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATA_DOG_SITE,
    service: import.meta.env.VITE_APP_NAME,
    version: import.meta.env.VITE_APP_VERSION,
    env: import.meta.env.MODE,
    sessionSampleRate: Number(import.meta.env.VITE_SESSION_SAMPLE_RATE ?? 100),
    sessionReplaySampleRate: Number(
      import.meta.env.VITE_SESSION_REPLAY_SAMPLE_RATE ?? 100
    ),
    defaultPrivacyLevel: "mask-user-input",
    // Return false (or any falsy value) → event is discarded, never sent
    beforeSend: (event) =>
      !EXCLUDED_EMAILS_FROM_TRACKING.has(event?.usr?.id || "") // We assign `usr.id` with user's email, hence, checking the `id` against the email blacklist
  });
}

async function enableMocking() {
  if (import.meta.env.VITE_API_MOCKING === "enabled") {
    const { worker } = await import("./mocks/browser.ts");

    const basePath = getBasePath();
    // Slash-less scope so the worker also controls "/intelligence" (no slash).
    const scope = basePath.slice(0, -1) || "/";

    return worker.start({
      serviceWorker: {
        url: withBasePath("mockServiceWorker.js"),
        options: { scope }
      },
      onUnhandledRequest: "bypass"
    });
  }
}

// TODO: reenable when the service worker config is ready
// const registerServiceWorker = async () => {
//   if (import.meta.env.PROD && "serviceWorker" in navigator) {
//     try {
//       const basePath = import.meta.env.VITE_BASE_PATH || "/";
//       const normalizedBasePath = basePath.startsWith("/")
//         ? basePath
//         : `/${basePath}`;
//       const servicePath = `${normalizedBasePath}service_worker.js`.replace(
//         "//",
//         "/"
//       );

//       const registration = await navigator.serviceWorker.register(servicePath, {
//         scope: normalizedBasePath.endsWith("/")
//           ? normalizedBasePath
//           : `${normalizedBasePath}/`
//       });
//       if (registration.installing) {
//         console.log("Service worker installing");
//       } else if (registration.waiting) {
//         console.log("Service worker installed");
//       } else if (registration.active) {
//         console.log("Service worker active");
//       }
//     } catch (error) {
//       console.error(`Registration failed with ${error}`);
//     }
//   }
// };
// registerServiceWorker();

const unregisterServiceWorker = () => {
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    const normalizedBasePath = getBasePath();

    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        // Check if the service worker scope matches our application's base path
        if (
          registration.scope.includes(
            `${window.location.origin}${normalizedBasePath}`
          )
        ) {
          console.log(
            `Unregistering service worker with scope: ${registration.scope}`
          );
          registration.unregister();
        }
      });
    });
  }
};

unregisterServiceWorker();

enableMocking().then(async () => {
  // Run the storage migration + open the readiness gate BEFORE render, so
  // redux-persist rehydrates from the correct (namespaced) per-tab keys.
  await initTabStorage();

  const appSubdomain = getSubdomain();

  if (shouldSkipPartnerCheck(appSubdomain)) {
    // Partner detail fetching is skipped on non-partner hosts, so persisted partner data
    // would not be refetched or replaced and could keep showing stale partner branding.
    // Prune only that entry — other persisted queries (countries, admin-area
    // hierarchies) must survive reloads.
    await removePersistedQueries(
      (queryKey) => queryKey[0] === "getPartnerDetail"
    );
  } else {
    try {
      await queryClient.fetchQuery({
        queryKey: getPartnerDetailQueryKey(appSubdomain),
        queryFn: () => getPartnerDetail(appSubdomain),
        meta: { shouldPersist: true },
        staleTime: 24 * 60 * 60 * 1000
      });
    } catch {
      queryClient.removeQueries({
        queryKey: getPartnerDetailQueryKey(appSubdomain)
      });
      window.location.href = import.meta.env.VITE_APP_URL;
      return;
    }
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
});
